import { Popover } from '@mantine/core';
import type { JSX, ReactNode } from 'react';
import { Fragment, memo } from 'react';
import styled, { css } from 'styled-components';
import { slugify } from '../../utils/slugify';
import type { ITheme } from '../../utils/theme';
import type { RIconComponent } from '../remix-icon';
import PopoverAction from './popover-action';

export interface Action<T = void> {
  handleClick: (data?: T) => void;
  label: string;
  ariaLabel?: string;
  description?: string;
  shouldShow: boolean;
  isDisabled?: boolean;
  disabledTooltip?: string;
  icon?: RIconComponent;
}

export interface ActionsProps<T> {
  actions: Action<T>[];
  isDisabled?: boolean;
  data?: T;
  popoverAriaLabel?: string;
  renderCustomAction?: (action: Action<T>, data?: T) => ReactNode;
  onClickAction?: () => void;
  actionPadding?: number;
}

const PopoverContent = <T,>({
  actions,
  data,
  isDisabled,
  renderCustomAction,
  onClickAction,
  actionPadding,
}: ActionsProps<T>): JSX.Element => {
  const actionsToShow = actions.filter((action) => action.shouldShow);

  return (
    <StyledPopoverContent $hasCustomAction={!!renderCustomAction}>
      {actionsToShow.map((action, index) =>
        renderCustomAction ? (
          renderCustomAction(action, data)
        ) : (
          <Fragment key={action.label}>
            <PopoverAction
              data-testid={slugify(action.label)}
              ariaLabel={action.ariaLabel ?? action.label}
              label={action.label}
              padding={actionPadding}
              handleClick={() => {
                action.handleClick(data);
                onClickAction?.();
              }}
              data={data}
              isDisabled={isDisabled || action.isDisabled}
              disabledTooltip={action.disabledTooltip}
            />
            {index < actionsToShow.length - 1 && <Divider />}
          </Fragment>
        )
      )}
    </StyledPopoverContent>
  );
};

export default memo(PopoverContent) as typeof PopoverContent;

const Divider = styled.div`
  ${({ theme }) => css`
    border-top: 1px solid ${theme.palette.neutral[50]};
    width: 100%;
  `}
`;

const StyledPopoverContent = styled(Popover.Dropdown)`
  ${({
    theme,
    $hasCustomAction,
  }: {
    $hasCustomAction: boolean;
    theme: ITheme;
  }) => css`
    overflow: hidden;
    border-radius: ${theme.borderRadius.sm};
    background-color: ${theme.palette.neutral['00']};
    border: 0.5px solid ${theme.palette.neutral[70]};
    display: flex;
    flex-direction: column;
    padding: ${theme.spacing(0)};
    min-width: 260px;
    z-index: 3000;
    box-shadow: ${$hasCustomAction
      ? `0px 4px 5px 0px ${theme.palette.black(0.05)}`
      : 'none'};
  `}
`;
